import { PRODUCT_IMAGE_URL, BackendType, BACKEND_DOMAINS, BACKEND_NAMES, } from "./consts.js";
import { Robotoff } from "./robotoff.js";
import { TAXONOMY_URL } from "./taxonomy/api.js";
import { ProductOpenerApiV2, getProductNameInLang, getProductIngredientsInLang, } from "./off-v2.js";
import { ProductOpenerApiV3 } from "./off-v3.js";
export { getProductNameInLang, getProductIngredientsInLang };
import { VERSION } from "./version.js";
/** Wrapper of OFF API */
export class OpenFoodFacts {
    /**
     * Create OFF object
     * @param fetch - Fetch implementation to use
     * @param options - Options for the OFF Object
     */
    constructor(fetch, options = { country: "world", language: "en" }) {
        ///////////
        // API V2
        ///////////
        this.performOCR = (barcode, photoId, ocrEngine) => this.apiv2.performOCR(barcode, photoId, ocrEngine);
        this.search = (query) => this.apiv2.search(query);
        /**
         * Returns all available attribute groups
         * @returns A promise that resolves to an array of attribute groups
         */
        this.getAttributeGroups = () => this.apiv2.getAttributeGroups();
        /**
         * Returns product attributes for a given barcode
         * @param barcode - The barcode of the product
         * @returns A promise that resolves to an array of product attributes
         */
        this.getProductAttributes = (barcode) => this.apiv2.getProductAttributes(barcode);
        /**
         * Returns product details by barcode with optional fields
         * @param barcode - The barcode of the product
         * @param query - An optional query object to filter the returned fields
         * @template T - An array of keys from ProductV3 to return
         * @example
         * ```typescript
         * const result = await api.getProductV3("1234567890123", { fields: ["product_name", "brands"] });
         * console.log(result.product.product_name, result.product.brands);
         * ```
         * @returns A promise that resolves to a product object with the specified fields or undefined if not found
         */
        this.getProductV3 = (barcode, query) => this.apiv3.getProductV3(barcode, query);
        /**
         * Adds or edits a product using the V2 API
         * @param product - The product data to add or edit
         * @param credentials - Optional credentials for authentication
         * @returns A promise that resolves to true if successful, false otherwise
         */
        this.addOrEditProductV2 = (product, credentials) => {
            var _a, _b;
            const username = (_a = credentials === null || credentials === void 0 ? void 0 : credentials.username) !== null && _a !== void 0 ? _a : this.defaultOptions.username;
            const password = (_b = credentials === null || credentials === void 0 ? void 0 : credentials.password) !== null && _b !== void 0 ? _b : this.defaultOptions.password;
            const nullableCredentials = username != null && password != null ? { username, password } : undefined;
            return this.apiv2.addOrEditProductV2(product, nullableCredentials);
        };
        /**
         * Uploads an image to OpenFoodFacts for a product.
         * @param barcode - The barcode of the product
         * @param imageFile - The image file to upload
         * @param imagefield - The field name for the image (e.g., "front", "ingredients", "nutrition")
         * @returns A promise that resolves to the upload response
         */
        this.uploadImage = (barcode, imageFile, imagefield) => this.apiv2.uploadImage(barcode, imageFile, imagefield);
        /**
         * Crops and selects an image for a product
         * @param barcode - The barcode of the product
         * @param imgid - Identifier of the image to select (should be a number)
         * @param id - Identifier of the selected image field (format: {IMAGE_TYPE}_{LANG})
         * @param cropData - Crop coordinates and options
         * @returns A promise that resolves to the crop response
         */
        this.cropImage = (barcode, imgid, id, cropData) => this.apiv2.cropImage(barcode, imgid, id, cropData);
        /**
         * Rotates an image for a product
         * @param barcode - The barcode of the product
         * @param id - Identifier of the selected image field (format: {IMAGE_TYPE}_{LANG})
         * @param imgid - Identifier of the image to rotate (should be a number as string)
         * @param angle - Angle of rotation in degrees (90, 180, or 270 clockwise)
         * @returns A promise that resolves to the rotation response
         */
        this.rotateImage = (barcode, id, imgid, angle) => this.apiv2.rotateImage(barcode, id, imgid, angle);
        /**
         * Unselects an image for a product
         * @param barcode - The barcode of the product
         * @param id - Image field (image id) of the photo to unselect (e.g., "front_fr")
         * @returns A promise that resolves to the unselect response
         */
        this.unselectImage = (barcode, id) => this.apiv2.unselectImage(barcode, id);
        /**
         * Deletes an uploaded image for a product
         * @param barcode - The barcode of the product corresponding to the image
         * @param imgid - The id of the image to be deleted
         * @returns A promise that resolves to the deletion response
         */
        this.deleteProductImage = (barcode, imgid) => this.apiv3.deleteProductImage(barcode, imgid);
        /**
         *
         * @param barcode
         * @param params
         * @returns
         */
        this.uploadProductImage = (barcode, params) => this.apiv3.uploadProductImage(barcode, params);
        /**
         * Returns product data using the V2 API
         * @param barcode - The barcode of the product
         * @returns A promise that resolves to the product data or undefined if not found
         */
        this.getProductV2 = (barcode) => this.apiv2.getProductV2(barcode);
        /**
         * Returns an array of image names for the product
         * @param barcode - The barcode of the product
         * @returns A promise that resolves to an array of image names or null if not found
         */
        this.getProductImages = (barcode) => this.apiv2.getProductImages(barcode);
        this.validateOptions(options);
        this.backendType = options.type;
        this.baseUrl = this.createBaseUrl(options);
        this.customUserAgent = this.createUserAgent();
        this.accessToken = options.accessToken;
        this.fetch = this.createFetchWrapper(fetch, options);
        this.defaultOptions = {
            lang: options.language,
            country: options.country,
            username: undefined,
            password: undefined,
        };
        this.apiv2 = new ProductOpenerApiV2(this.fetch, { host: this.baseUrl });
        this.apiv3 = new ProductOpenerApiV3(this.fetch, { host: this.baseUrl });
        this.robotoff = new Robotoff(fetch);
    }
    /**
     * Validates constructor options for mutual exclusivity
     */
    validateOptions(options) {
        if ((options.host && options.country) ||
            (options.type && options.country)) {
            throw new Error("You must provide either `host`, `type`, or `country`, not multiple.");
        }
    }
    /**
     * Creates the base URL based on options
     */
    createBaseUrl(options) {
        if (options.host != null) {
            return options.host;
        }
        if (options.type != null) {
            const domain = BACKEND_DOMAINS[options.type];
            return `https://world.${domain}`;
        }
        return `https://${options.country || "world"}.openfoodfacts.org`;
    }
    /**
     * Creates the User-Agent string based on backend type
     */
    createUserAgent() {
        if (this.backendType != null) {
            const backendName = BACKEND_NAMES[this.backendType];
            return `${backendName} - NodeJS ${VERSION}`;
        }
        return `OpenFoodFacts - NodeJS ${VERSION}`;
    }
    /**
     * Validates access token format and expiration
     */
    validateAccessToken(token) {
        if (typeof token !== "string") {
            throw new Error("Access token must be a string.");
        }
        if (token.length === 0) {
            throw new Error("Access token cannot be an empty string.");
        }
        if (!/^[A-Za-z0-9-_.]+$/.test(token)) {
            throw new Error("Access token can only contain alphanumeric characters, dashes, underscores, and periods.");
        }
        if (this.isTokenExpired(token)) {
            throw new Error("Access token is expired.");
        }
    }
    /**
     * Creates a fetch wrapper with User-Agent and optional token handling
     */
    createFetchWrapper(fetch, options) {
        // Base fetch wrapper with User-Agent
        let wrappedFetch = this.createUserAgentFetch(fetch);
        // Add token handling if access token is provided
        if (options.accessToken != null) {
            this.validateAccessToken(options.accessToken);
            wrappedFetch = this.createTokenAwareFetch(wrappedFetch, options);
        }
        return wrappedFetch;
    }
    /**
     * Creates a fetch wrapper that adds User-Agent header
     */
    createUserAgentFetch(fetch) {
        return (url, init) => {
            const headers = new Headers(init === null || init === void 0 ? void 0 : init.headers);
            headers.set("User-Agent", this.customUserAgent);
            return fetch(url, Object.assign(Object.assign({}, init), { headers }));
        };
    }
    /**
     * Creates a fetch wrapper that handles token refresh and authorization
     */
    createTokenAwareFetch(fetch, options) {
        return async (url, init) => {
            const headers = new Headers(init === null || init === void 0 ? void 0 : init.headers);
            if (this.accessToken == null) {
                throw new Error("Access token was first specified and now is null.");
            }
            if (this.isTokenExpired(this.accessToken)) {
                this.accessToken = await this.refreshAccessToken(options);
            }
            headers.set("Authorization", `Bearer ${this.accessToken}`);
            return fetch(url, Object.assign(Object.assign({}, init), { headers }));
        };
    }
    /**
     * Refreshes the access token using the provided callback
     */
    async refreshAccessToken(options) {
        if (options.onAccessTokenExpired == null) {
            throw new Error("Access token expired and no handler provided to refresh it." +
                " You should provide `onAccessTokenExpired` option or wrap the fetch function to handle token expiration.");
        }
        const newAccessToken = await options.onAccessTokenExpired();
        if (newAccessToken == null) {
            throw new Error("onAccessTokenExpired handler did not return a new access token.");
        }
        return newAccessToken;
    }
    isTokenExpired(token) {
        const parts = token.split(".");
        if (parts.length !== 3) {
            throw new Error("Invalid JWT token format");
        }
        const payload = JSON.parse(Buffer.from(parts[1], "base64").toString("utf-8"));
        // Check if the token is expired
        return payload.exp && Date.now() >= payload.exp * 1000;
    }
    ////////////////
    // TAXONOMIES
    ////////////////
    getBrand(brandName) {
        return this.apiv2.getTaxoEntry("brands", brandName);
    }
    getLanguage(languageName) {
        return this.apiv2.getTaxoEntry("languages", languageName);
    }
    getBrands() {
        return this.getTaxo("brands");
    }
    getLanguages() {
        return this.getTaxo("languages");
    }
    getLabels() {
        return this.getTaxo("labels");
    }
    getAdditives() {
        return this.getTaxo("additives");
    }
    getAllergens() {
        return this.getTaxo("allergens");
    }
    getCategories() {
        return this.getTaxo("categories");
    }
    getCountries() {
        return this.getTaxo("countries");
    }
    getIngredients() {
        return this.getTaxo("ingredients");
    }
    getPackagings() {
        return this.getTaxo("packaging");
    }
    getStates() {
        return this.getTaxo("states");
    }
    getStores() {
        return this.getTaxo("stores");
    }
    getNutrients() {
        return this.getTaxo("nutrients");
    }
    async getTaxo(taxo) {
        const res = await this.fetch(TAXONOMY_URL(taxo));
        return (await res.json());
    }
    async getFacet(facet, opts) {
        const params = new URLSearchParams();
        if (opts === null || opts === void 0 ? void 0 : opts.page)
            params.set("page", `${opts.page}`);
        if (opts === null || opts === void 0 ? void 0 : opts.pageSize)
            params.set("page_size", `${opts.pageSize}`);
        if (opts === null || opts === void 0 ? void 0 : opts.sortBy)
            params.set("sort_by", opts.sortBy);
        const res = await this.fetch(`${this.baseUrl}/facets/${facet}.json?${params}`);
        return (await res.json());
    }
    async getFacetValue(facet, value, opts) {
        const params = new URLSearchParams();
        if (opts === null || opts === void 0 ? void 0 : opts.page)
            params.set("page", `${opts.page}`);
        if (opts === null || opts === void 0 ? void 0 : opts.pageSize)
            params.set("page_size", `${opts.pageSize}`);
        if (opts === null || opts === void 0 ? void 0 : opts.sortBy)
            params.set("sort_by", opts.sortBy);
        const res = await this.fetch(new URL(`${this.baseUrl}/facets/${facet}/${value}.json?${params}`));
        return (await res.json());
    }
    async getLoginStatus() {
        const response = await this.fetch(new URL("/cgi/auth.pl?body=1", this.baseUrl));
        if (!response.ok)
            return undefined;
        return (await response.json());
    }
}
export default OpenFoodFacts;
/**
 * Gets URL for a product image based on its barcode and image name
 * @param barcode - Product barcode
 * @param imageName - Name of the image (e.g., "front", "ingredients", "nutrition")
 * @param images - Image metadata from product data
 * @param size - Image size (100, 200, 400, or full) - defaults to 400
 * @returns Complete URL to the specific image or null if not found
 */
export function getProductImageUrl(barcode, imageName, images, size = "400") {
    const paddedBarcode = barcode.toString().padStart(13, "0");
    const match = paddedBarcode.match(/^(.{3})(.{3})(.{3})(.*)$/);
    if (!match) {
        throw new Error(`Invalid barcode format: ${paddedBarcode}`);
    }
    const path = `${match[1]}/${match[2]}/${match[3]}/${match[4]}`;
    const image = images[imageName];
    if (!image) {
        return null;
    }
    const rev = image.rev;
    let filename;
    if (rev) {
        filename = `${imageName}.${rev}.${size}.jpg`;
    }
    else {
        filename = `${imageName}.${size}.jpg`;
    }
    return PRODUCT_IMAGE_URL(`${path}/${filename}`);
}
//# sourceMappingURL=off.js.map